import type { AccessibleFolderMeta } from '../stores/accessible-folder-cache';

export type CachedFolderAreaKind = 'app' | 'private' | 'public' | 'teamspace';

export type CachedFolderAreaResolver = (
  folder: AccessibleFolderMeta
) => CachedFolderAreaKind | null;

function systemKindArea(
  folder: Pick<AccessibleFolderMeta, 'isWorkspaceRoot' | 'systemKind'>
): CachedFolderAreaKind | null {
  if (folder.isWorkspaceRoot) {
    return 'public';
  }

  switch (folder.systemKind) {
    case 'private_area_roots':
    case 'private_root':
    case 'private_mail':
      return 'private';
    case 'teamspace_root':
      return 'teamspace';
    case 'app_area_root':
    case 'app_base_folder':
      return 'app';
    default:
      return null;
  }
}

/**
 * Resolve canonical sidebar placement from the folder ancestry.
 *
 * Folder visibility is deliberately absent: Visible/Invisible controls
 * discoverability for actors without effective access, never area placement.
 */
export function createCachedFolderAreaResolver(
  folders: readonly AccessibleFolderMeta[]
): CachedFolderAreaResolver {
  const foldersById = new Map(folders.map(item => [item.id, item]));
  const memo = new Map<string, CachedFolderAreaKind | null>();

  return folder => {
    const path: AccessibleFolderMeta[] = [];
    const visited = new Set<string>();
    let current: AccessibleFolderMeta | undefined = folder;
    let resolved: CachedFolderAreaKind | null = null;

    while (current) {
      if (memo.has(current.id)) {
        resolved = memo.get(current.id) ?? null;
        break;
      }
      if (visited.has(current.id)) {
        resolved = null;
        break;
      }
      visited.add(current.id);
      path.push(current);

      const area = systemKindArea(current);
      if (area) {
        resolved = area;
        break;
      }
      if (current.parentId === null) {
        resolved = 'public';
        break;
      }
      current = foldersById.get(current.parentId);
    }

    for (const item of path) {
      memo.set(item.id, resolved);
    }
    return resolved;
  };
}

export function resolveCachedFolderArea(
  folder: AccessibleFolderMeta,
  folders: readonly AccessibleFolderMeta[]
): CachedFolderAreaKind | null {
  return createCachedFolderAreaResolver(folders)(folder);
}
